import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, Mail, MapPin, Award, Users, Linkedin, ArrowRight, Briefcase, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Team = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const managementTeam = [
    {
      name: 'Sylvester Mensah',
      role: 'Managing Director',
      image: 'https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/2ms9wt79_Sylvester.jpg',
      description: 'Business technology executive with proven ability to successfully manage enterprise-wide IT implementations. Expert in strategic planning, business development, and organizational leadership.',
      certifications: ['Strategic Planning', 'Business Development', 'IT Leadership']
    },
    {
      name: 'Kim Mensah',
      role: 'IT Manager',
      image: 'https://customer-assets.emergentagent.com/job_landing-evolution-1/artifacts/v05tz5x0_IT.jpg',
      description: 'Experienced IT professional with comprehensive knowledge in technology infrastructure, systems management, and IT operations. Drives technological innovation and excellence.',
      certifications: ['IT Infrastructure', 'Systems Management', 'Technology Leadership']
    },
    {
      name: 'Victoria Ageyman',
      role: 'Customer Service Manager',
      image: 'https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/ogavi9rv_Henery%20Danso.jpg',
      description: 'Dedicated professional ensuring exceptional customer service and client satisfaction. Expert in managing customer relationships and service excellence.',
      certifications: ['Customer Service Excellence', 'Client Relations Management']
    },
    {
      name: 'Upjohn Kofi Sangur',
      role: 'Finance Manager',
      image: 'https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/c9lvq5yn_Upjohn%20Kofi%20Sangur.jpg',
      description: 'Financial expert with extensive experience in corporate finance, budgeting, and financial planning. Ensures fiscal responsibility and strategic financial management.',
      certifications: ['Financial Management', 'Corporate Finance', 'Budget Planning']
    },
    {
      name: 'Henry Danso Mensah',
      role: 'General Manager',
      image: 'https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/iyn2oq3f_Henry%20Danso%20Mensah.jpg',
      description: 'Seasoned executive with comprehensive experience in business operations, strategic planning, and organizational leadership. Drives company growth and operational excellence.',
      certifications: ['Business Management', 'Strategic Leadership', 'Operations Management']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                alt="HNS Logo"
                className="h-14 w-14 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-[#18246F]">HNS</h1>
                <p className="text-xs text-gray-600">...technology with infinite solutions</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Home</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">About</Link>
              <Link to="/services" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Services</Link>
              <Link to="/projects" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Projects</Link>
              <Link to="/team" className="text-[#18246F] font-semibold">Team</Link>
              <Link to="/contact" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Contact</Link>
              <Button onClick={() => navigate('/contact')} className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white">Let's Connect</Button>
            </nav>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-700">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Home</Link>
              <Link to="/about" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">About</Link>
              <Link to="/services" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Services</Link>
              <Link to="/projects" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Projects</Link>
              <Link to="/team" className="block px-4 py-2 text-[#18246F] bg-gray-100 rounded-lg font-semibold">Team</Link>
              <Link to="/contact" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Contact</Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#18246F] via-[#293074] to-[#3d4a8f] text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="mb-4 bg-white/20 text-white border-white/30">Our Team</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Leadership & Management Team</h1>
          <p className="text-xl text-white/90 max-w-3xl">Experienced professionals driving innovation and excellence in IT solutions</p>
        </div>
      </div>

      {/* Team Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#18246F] mb-4">Management <span className="text-[#3C74FF]">Team</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Meet the experts behind our success</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {managementTeam.map((member, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#3C74FF] hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="w-32 h-32 rounded-full mx-auto mb-4 overflow-hidden border-4 border-[#3C74FF]/20">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardTitle className="text-center text-xl">{member.name}</CardTitle>
                  <p className="text-center text-[#8B2635] font-semibold">{member.role}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed text-center">{member.description}</p>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 mb-3 text-center">Certifications & Expertise:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {member.certifications.map((cert, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{cert}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#18246F] mb-4">Why Work <span className="text-[#3C74FF]">With Us</span></h2>
            <p className="text-gray-600">Our team culture and values</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center hover:shadow-xl transition-all">
              <CardContent className="pt-8">
                <div className="bg-[#3C74FF]/10 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Award size={32} className="text-[#3C74FF]" />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Excellence</h3>
                <p className="text-sm text-gray-600">Committed to delivering the highest quality in every project</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all">
              <CardContent className="pt-8">
                <div className="bg-[#3C74FF]/10 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users size={32} className="text-[#3C74FF]" />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Collaboration</h3>
                <p className="text-sm text-gray-600">Working together to achieve common goals</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all">
              <CardContent className="pt-8">
                <div className="bg-[#3C74FF]/10 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Briefcase size={32} className="text-[#3C74FF]" />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Professionalism</h3>
                <p className="text-sm text-gray-600">Maintaining the highest standards of integrity</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all">
              <CardContent className="pt-8">
                <div className="bg-[#3C74FF]/10 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-[#3C74FF]" />
                </div>
                <h3 className="font-bold text-[#18246F] mb-2">Reliability</h3>
                <p className="text-sm text-gray-600">Consistent delivery and dependable service</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#18246F] to-[#3C74FF] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Join Our Team</h2>
          <p className="text-xl text-white/90 mb-8">We're always looking for talented professionals to join our growing team</p>
          <Button 
            onClick={() => navigate('/#contact')}
            size="lg"
            className="bg-white text-[#18246F] hover:bg-gray-100 group"
          >
            Get in Touch
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#18246F] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                  alt="HNS Logo"
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold">HNS</h3>
                  <p className="text-xs text-gray-300">...technology with infinite solutions</p>
                </div>
              </Link>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Leading the ICT industry with reliable information systems solutions. ISO 9001:2015 certified with over 20 years of excellence in technology delivery.
              </p>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Award size={16} />
                <span>Trusted by 50+ Organizations</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/projects" className="hover:text-white transition-colors">Projects</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Team</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Network Security</li>
                <li>Cloud Management</li>
                <li>Software Development</li>
                <li>VoIP Solutions</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <Mail size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>info@hnsgh.com</span>
                </li>
                <li className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>Accra, Ghana | Manassas, VA USA</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Hyperlink Network Systems Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Call Button */}
      <a 
        href="tel:+15714064203"
        className="fixed bottom-8 left-8 bg-[#3C74FF] hover:bg-[#2d5fd9] text-white rounded-full p-4 shadow-2xl z-50 flex items-center gap-3 group transition-all duration-300 hover:scale-110"
      >
        <Phone size={24} className="animate-pulse" />
        <span className="hidden group-hover:inline-block text-sm font-semibold whitespace-nowrap">
          +1 (571) 406-4203
        </span>
      </a>
    </div>
  );
};

export default Team;
